<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {ref, watch} from "vue";
import {useContratStore} from "@/stores/Contrat.js";
import AdvancedContratFilter from "@/views/Contrat/Components/ContratFilter.vue";
import {storeToRefs} from "pinia";

import axios from "axios";

export default {
    name: "Contrats",
    components: {AdvancedContratFilter},
    setup() {
        const {
            loading,
            total,
            itemsPerPage,
        } = useSharedData();

        const {getBack, transformFilterValues, formatedDate, getStatusColor} = useSharedMethod();

        const {$router, $i18n} = useTools();

        const contratStore = useContratStore();
        const securityStore = useSecurityStore();
        const {idControlProgram, idIssue, selectedModule} = storeToRefs(securityStore);

        const filter = ref({
            duration: null,
            periodicity: null,
            terminated: null,
            status: ["ACTIVE", "PENDING"],
            programIds: idControlProgram.value && selectedModule.value && selectedModule.value === 'contrat' ? [idControlProgram.value] : []
        });


        const headers = [
            {
                text: "...",
                align: "start",
                sortable: false,
                value: "action",
            },
            {
                text: $i18n.t("contrat.number"),
                align: "start",
                sortable: true,
                value: "number",
            },

            {
                text: $i18n.t("module.worker"),
                align: "start",
                sortable: false,
                value: "workerLastName",
            },
            {
                text: $i18n.t("module.state"),
                align: "start",
                sortable: true,
                value: "status",
            },
            {
                text: $i18n.t("module.contrat_template"),
                align: "start",
                sortable: true,
                value: "contratTemplateName",
            },
            {
                text: $i18n.t("contrat.start"),
                align: "start",
                sortable: true,
                value: "start",
            },
            {
                text: $i18n.t("contrat.end"),
                align: "start",
                sortable: true,
                value: "end",
            },
            {
                text: $i18n.t("contrat.duration"),
                align: "start",
                sortable: true,
                value: "duration",
            },
            {
                text: $i18n.t("module.contrat_characteristic"),
                align: "start",
                sortable: true,
                value: "natureName",
            },
            {
                text: $i18n.t('contrat.trial_period_status_title'),
                align: "start",
                sortable: true,
                value: "trialPeriodStatus",
            },
            {
                text: $i18n.t("contrat.trial_period_duration"),
                align: "start",
                sortable: true,
                value: "trialPeriodDuration",
            },
            {
                text: $i18n.t('contrat.trial_period_end'),
                align: "start",
                sortable: true,
                value: "trialPeriodEnd",
            },
            {
                text: $i18n.t("contrat.end_renewed_trial_period"),
                align: "start",
                sortable: true,
                value: "endRenewedTrialPeriod",
            },
            {
                text: $i18n.t("contrat.contrat_leaving_reason_name"),
                align: "start",
                sortable: true,
                value: "exitRationaleName",
            },
            {
                text: $i18n.t('contrat.leaving_date'),
                align: "start",
                sortable: true,
                value: "leavingDate",
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: (item) => item.updateOption,
                disabled: (item) => item.status === 'ARCHIVE' || item.status === 'TERMINATED',
                func: (item) => {
                    $router.push({name: 'contrats.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: (item) => item.deleteOption,
                disabled: (item) => item.status === 'ARCHIVE' || item.status === 'TERMINATED',
                func: (item) => {
                    $router.push({name: 'contrats.delete', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.renew'),
                icon: 'mdi-autorenew',
                color: 'teal',
                access: (item) => item.updateOption,
                disabled: (item) => item.status === 'PENDING',
                func: (item) => {
                    $router.push({name: 'contrats.renew', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('contrat.handle_trial_period'),
                icon: 'mdi-vector-polyline',
                color: 'blue darken-2',
                access: (item) => item.updateOption,
                disabled: (item) => item.status !== 'ACTIVE' || ['VALIDATED', 'REJECTED', 'WITHOUT'].includes(item.trialPeriodStatus),
                func: (item) => {
                    $router.push({name: 'contrats.handle_trial_period', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('contrat.terminate'),
                icon: 'mdi-domain-remove',
                color: 'red',
                access: (item) => item.updateOption,
                disabled: (item) => item.status !== 'ACTIVE',
                func: (item) => {
                    $router.push({name: 'contrats.leaving', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.archive'),
                icon: 'mdi-archive',
                color: 'purple',
                access: (item) => item.updateOption && item.status === 'ACTIVE',
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrats.archive', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.unarchive'),
                icon: 'mdi-archive',
                color: 'purple',
                access: (item) => item.updateOption && item.status === 'ARCHIVE',
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrats.unarchive', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.activate'),
                icon: 'mdi-power-plug',
                color: 'blue darken-2',
                access: (item) => item.updateOption,
                disabled: (item) => item.status !== 'PENDING',
                func: (item) => {
                    $router.push({name: 'contrats.activate', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.print'),
                icon: 'mdi-printer',
                color: 'blue-grey',
                access: (item) => item.printOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrats.print', params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `contrats.documents`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.template_application"),
                icon: 'mdi-text-box-multiple',
                color: 'teal',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrats.template_applications', params: {id: item.id}})
                },
            },
        ];

        const searchParams = ref({
            sortBy: "end",
            descending: false,
            page: 1,
            itemsPerPage: itemsPerPage,
        });

        function handleSearch(params) {
            loading.value = true;
            contratStore.setContrats([]);
            total.value = 0;

            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/contrats', {params: transformFilterValues(filter.value)}).then(response => {
                contratStore.setContrats(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});


        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                if (key === 'status') {
                    filter.value[key] = ["ACTIVE", "PENDING"];
                } else {
                    filter.value[key] = null;
                }
            });
            idControlProgram.value = null;
            idIssue.value = null;
        }

        function view(item) {
            $router.push({name: "contrats.view", params: {id: item.id}});
        }

        const loadingExportExcel = ref(false);

        function exportExcel() {
            loadingExportExcel.value = true;
            axios.get('/contrats/exportExcel', {
                responseType: 'blob',
                params: transformFilterValues(filter.value),
            }).then(response => {
                // Create a new Blob object using the response data of the file
                const fileURL = window.URL.createObjectURL(new Blob([response.data]));
                const fileLink = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.setAttribute('download', 'contrats.xlsx'); // Set the file name for download
                document.body.appendChild(fileLink);

                fileLink.click(); // Programmatically trigger the download

                // Clean up by revoking the Object URL and removing the link element
                window.URL.revokeObjectURL(fileURL);
                fileLink.remove();
            }).finally(() => {
                loadingExportExcel.value = false;
            });
        }

        return {
            contratStore,
            securityStore,
            headers,
            loading,
            total,
            itemsPerPage,
            filter,
            loadingExportExcel,
            actions,
            searchParams,
            formatedDate,
            exportExcel,
            clearFilter,
            findAll,
            getBack,
            view,
            getStatusColor
        };
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.contrat')"
            show-total
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <AdvancedContratFilter v-model="filter"/>
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-export
                    :loading="loadingExportExcel"
                    @press="exportExcel"
                />
                <w-btn-add
                    :show="securityStore.authorities['SAVE_CONTRAT']"
                    @press="$router.push({name: 'contrats.add'})"
                />
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="contratStore.contrats"
                    :loading="loading"
                    :total="total"
                    root-path="contrats"
                    module="CONTRAT"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.number`]="{ item }">
                        <w-chip
                            :text="item.number"
                            :icon="item.workerHead ? 'mdi-account-supervisor-circle' : null"
                            icon-color="teal"
                        />
                    </template>
                    <template v-slot:[`item.workerLastName`]="{ item }">
                        <w-inline-text bold :text="item.workerName"/>
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <w-inline-text :text="$t(`contrat.${item.status}`)"/>
                    </template>
                    <template v-slot:[`item.contratTemplateName`]="{ item }">
                        <w-inline-text :text="item.contratTemplateName"/>
                    </template>
                    <template v-slot:[`item.start`]="{ item }">
                        <w-inline-text :text="formatedDate(item.start)"/>
                    </template>
                    <template v-slot:[`item.end`]="{ item }">
                        <w-chip color="teal" :text="formatedDate(item.end)"/>
                    </template>
                    <template v-slot:[`item.duration`]="{ item }">
                        <w-inline-text :text="item.determinedTime ? `${item.duration} ${$t(`common.${item.periodicity}`)}` : $t('contrat.cdi')"/>
                    </template>
                    <template v-slot:[`item.natureName`]="{ item }">
                        <w-inline-text :text="item.natureName"/>
                    </template>
                    <template v-slot:[`item.trialPeriodStatus`]="{ item }">
                        <w-chip
                            :color="getStatusColor(`CONTRAT_${item.trialPeriodStatus}`)"
                            :text="$t(`contrat.trial_period_status.${item.trialPeriodStatus}`)"
                        />
                    </template>
                    <template v-slot:[`item.trialPeriodDuration`]="{ item }">
                        <w-inline-text :text="item.trialPeriod ? `${item.trialPeriodDuration} ${$t(`common.${item.trialPeriodPeriodicity}`)}` : null"/>
                    </template>
                    <template v-slot:[`item.trialPeriodEnd`]="{ item }">
                        <w-inline-text :text="formatedDate(item.trialPeriodEnd)"/>
                    </template>
                    <template v-slot:[`item.endRenewedTrialPeriod`]="{ item }">
                        <w-inline-text :text="formatedDate(item.endRenewedTrialPeriod)"/>
                    </template>
                    <template v-slot:[`item.exitRationaleName`]="{ item }">
                        <w-inline-text clazz="red--text" bold :text="item.exitRationaleName"/>
                    </template>
                    <template v-slot:[`item.leavingDate`]="{ item }">
                        <w-inline-text clazz="red--text" bold :text="formatedDate(item.leavingDate)"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>